import { DTOUserLanguageEntry } from 'src/apis/dto/DTOUserLanguageEntry';

export enum GENDER {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  NONBINARY = 'NONBINARY',
}

export enum LoginType {
  FACEBOOK = 'FACEBOOK',
  GOOGLE = 'GOOGLE',
  APPLE = 'APPLE',
  SMS = 'SMS',
}

export interface RegisterParamsBase {
  birthYear?: number;
  birthMonth?: number;
  birthDay?: number;
  gender?: GENDER;
  // gender === NONBINARY이면 childGender는 필수
  childGender?: string;
  nickname?: string;
  consents: Record<string, string>; // 회원가입을 위해 유저가 동의한 필수 약관
  legalNoticesConfirmed: Record<string, string>; // 회원가입을 위해 유저가 동의한 필수 법적 고지 (privacyPolicy, cookiePolicy)

  /**
   * 탈퇴유예 상태의 사용자가 회원가입 시 기존 데이터를 삭제하고 새로 회원가입을 시도할 지 여부
   * true: 기존 데이터를 삭제하고 새로 회원가입을 시도한다.
   * false: 기존 데이터가 존재하지 않는 유저가 회원가입을 시도한다.
   *
   * 구버전 클라이언트 대응을 위해 API에서는 optional 필드이지만, 하위호환 대응이 불필요한 웹클라이언트에서는 required로 간주한다.
   */
  purgeBeforeSignUp: boolean;

  /**
   * 가입 과정에서 프로필 사진을 입력받았는지 여부
   */
  profileImageChosen: boolean;

  /**
   * 가입 과정의 프로필 사진 입력 단계에서 스킵을 눌렀는지 여부
   */
  profileImageSkipped?: boolean;

  /**
   * 가입 과정의 닉네임 입력 단계에서 스킵을 눌렀는지 여부
   */
  nicknameSkipped?: boolean;

  /**
   * 가입 과정에서 입력한 대화 언어
   */
  conversationLanguage?: {
    /**
     * 제1 언어
     */
    primaryLanguageCode: string;
    /**
     * 제2 언어
     */
    secondaryLanguageCode?: string;
  };

  /**
   * 가입 과정에서 입력한 선호 국가 코드
   */
  preferredCountryCode?: string;

  /**
   * SNS 회원가입에서 KMC 본인인증을 완료했을 때 signup 요청에 인라인으로 싣는 결과 키
   */
  kmcVerification?: {
    resultKey: string;
  };
}

export interface NonBinaryChildGender {
  key: string;
  localizedText: string;
}

export interface EthnicityOption {
  /**
   * 서버와 통신하기 위해 사용할 key값
   */
  key: string;

  /**
   * 유저에게 노출될 텍스트 (서버서 번역 적용)
   */
  displayName: string;
}

export interface EthnicitySettings {
  skipEnabled: boolean;
  ethnicities: ReadonlyArray<EthnicityOption>;
}

export const SMS_VERIFIED_METHOD_TYPES = {
  KMC: 'KMC',
  TSP: 'TSP',
} as const;

export type SmsVerifiedMethod =
  (typeof SMS_VERIFIED_METHOD_TYPES)[keyof typeof SMS_VERIFIED_METHOD_TYPES];

export interface SmsVerification {
  method: SmsVerifiedMethod;
}

export interface KmcInfo {
  redirectUrl?: string;
}

export interface SmsVerifiedSettings {
  preVerification?: boolean;
  mainSms?: SmsVerifiedMethod;
  subSms?: SmsVerifiedMethod;
  kmcInfo?: KmcInfo;
  /**
   * Apple 회원가입에서 KMC를 사용할지 여부.
   * true일 때만 Apple login 시 KMC를 띄우고, 그 외(false/null/undefined)는 TSP로 강등한다.
   * App Store 리뷰 리젝 방지를 위한 서버 스위치.
   */
  enabledSignupWithApple?: boolean;
}

export interface SignUpSettingsResponse {
  genderSettings: {
    nonBinaryChildGenders: Array<NonBinaryChildGender>;
    nonBinaryEnabled: boolean;
    hideGenderOptionEnabled: boolean;
    hideGenderDefaultValue: boolean;
  };
  birthdaySettings: {
    uiType: 'BIRTHDAY_WEB_B';
    blockAlertAge: number;
  };
  /**
   * primary language(제1 언어) 선택 단계에서 선택 가능한 국가 코드 목록
   *
   * 표시조건
   * - languageAndCountry InsufficientInformationException 에러 발생 시
   */
  preferredLanguageSettings?: {
    preferredLanguages: DTOUserLanguageEntry[];
    maxPreferredLanguageCount: number;
  };
  /**
   * secondary language(제2 언어) 선택 단계에서 선택 가능한 언어 코드 목록
   *
   * 표시조건 (AND 조건)
   * - languageAndCountry InsufficientInformationException 에러 발생 시
   * - conversationLanguageSettings.showSecondaryLanguage가 true인 경우
   */
  conversationLanguageSettings?: {
    languageOptions: DTOUserLanguageEntry[];
    showSecondaryLanguage: boolean;
  };

  /**
   * 가입 과정에서 표시할 선호 국가 코드 목록
   *
   * 표시조건 (AND 조건)
   * - languageAndCountry InsufficientInformationException 에러 발생 시
   * - 유저가 선택한 제1언어 혹은 제2언어가 funnelTargetLanguages 목록에 포함되어 있는 경우
   * - preferredCountrySettings가 truthy하게 떨어지는 경우
   */
  preferredCountrySettings?: {
    funnelTargetLanguages: string[];
    countryOptions: Array<{ code: string; displayName: string }>;
  };
  /**
   * Truthy하게 떨어지는 경우 가입 과정에서 민족성 정보 단계 추가
   */
  ethnicitySettings?: EthnicitySettings;
  nicknameSettings?: {
    showNicknameSkipButton: boolean;
  };
  profileImageSettings?: {
    skipEnabled: boolean;
  };
  smsVerifiedSettings: SmsVerifiedSettings;
}
